import { Button, ButtonGroup, Dropdown, DropdownButton, Modal } from "react-bootstrap"
import React, { useEffect, useState } from "react"
import { defineMessages, FormattedMessage, injectIntl } from "react-intl"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend/index"
import Icon from "../icon/icon.component"
import "react-phone-number-input/style.css"
import PhoneInput from "react-phone-number-input"
import { E164Number } from "libphonenumber-js"
import { isValidPhoneNumber } from "react-phone-number-input"
import GlobalHelper from "../GlobalHelper"
import ActionButton from "../button/actionbutton.component"
import SmsInput from "../../question-conversation/components/SmsInput"
import Template from "../../state/template/Template"
import templateService from "../../state/template/TemplateService"
import { TYPE_SMS } from "../../state/conversation/ConversationThreadTypes"
import { isError } from "../../state/helpers/Error"
import { Intl } from "../../state/helpers/IntlProps"

const messages = defineMessages({
    phonenumber_placeholder: {
        id: "send_sms.modal.phone_number_placeholder",
        defaultMessage: "Phone Number",
    },
    question_placeholder: {
        id: "send_sms.modal.textarea_placeholder",
        defaultMessage: "Type your message here...",
    },
})

type Props = {
    intl: Intl
    phoneNumber: string
    projectId: string,
    accountId: string,
    contactId: string,
    templateType: string,
    countryCode: string,
    onClose: () => void
    onSubmit: () => void
}

const SendSmsModal = (props: Props) => {
    const [phoneNumber, setPhoneNumber] =
        useState<string | E164Number | undefined>(GlobalHelper.getInternationalPhoneNumber(props.phoneNumber, props.countryCode))
    const [templatesList, setTemplatesList] = useState<Template[]>([])
    const [selectedTemplate, setSelectedTemplate] = useState<Template>()
    const [content, setContent] = useState<string>("")
    const [imageIds, setImageIds] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [hasError, setError] = useState<boolean>(false)

    useEffect(() => {
        if (props.templateType) {
            templateService.findByTemplateKey(props.templateType).then(t => {
                if (!isError(t)) {
                    setSelectedTemplate(t)
                    setContent(t.getContent())
                }
            })
            templateService.findAllTemplates(TYPE_SMS, props.templateType).then(templates => {
                if (!isError(templates)) {
                    setTemplatesList(templates)
                }
            })
        }
    }, [props.templateType])

    const sendSmsMessage = () => {
        setIsLoading(true)
        if (phoneNumber && isValidPhoneNumber(phoneNumber?.toString())) {
            const data = {
                sender: "seller",
                content: content,
                imageIds: imageIds,
                sellerId: AppContext.getUserLoggedIn().id,
                customerId: props.contactId,
                customerPhoneNumber: phoneNumber,
                conversationThreadType: TYPE_SMS,
                accountId: props.accountId,
            }

            AppContext.getApiClient().post(`/projects/${props.projectId}/messages`, data).then(res => {
                if (!res.error) {
                    setIsLoading(false)
                    setError(false)
                    props.onSubmit()
                    props.onClose()
                } else {
                    setError(true)
                    setIsLoading(false)
                }
            })
        }
    }

    const handleTemplateSelect = (event) => {
        setSelectedTemplate(templatesList[event])
        setContent(templatesList[event].getContent())
    }

    const onContentCallback = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setContent(e.target.value)
    }

    const disableSubmit = isLoading ||
        (!content && imageIds.length === 0) ||
        !phoneNumber ||
        !GlobalHelper.isTwilioNumberValid(phoneNumber)

    return (
        <Modal size="lg" show={true} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FormattedMessage
                        id="send_sms.modal.modal.title"
                        defaultMessage="Send an SMS"
                    />
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="container-fluid">
                    <div className="form-group row mt-3">
                        <label className="col-lg-3 col-12 col-form-label">
                            <FormattedMessage
                                id="send_sms.modal.to_label"
                                defaultMessage="To"
                            />
                        </label>

                        <div className="col-lg-9 col-12">
                            <PhoneInput
                                value={phoneNumber}
                                onChange={(value) => setPhoneNumber(value)}
                                placeholder={
                                    props.intl.formatMessage(messages["phonenumber_placeholder"])
                                }
                            />
                        </div>
                    </div>

                    <div className="form-group row mt-3">
                        <label className="col-lg-3 col-12 col-form-label">
                            <FormattedMessage
                                id="send_sms.modal.content_label"
                                defaultMessage="Content"
                            />
                        </label>

                        <div className={"col-lg-9 col-12"}>
                            <SmsInput
                                threadType={TYPE_SMS}
                                accountId={props.accountId}
                                images={imageIds}
                                content={content}
                                onImageCallback={setImageIds}
                                onContentCallback={onContentCallback}
                            />
                        </div>
                    </div>
                    {hasError &&
                        <div className="text-danger mb-2 p-2 text-right">
                            <small>
                                <Icon value={"exclamation"} className={"mr-1"} />

                                <FormattedMessage
                                    id="send_sms.modal.error"
                                    defaultMessage="There was an error sending your message"
                                />
                            </small>
                        </div>
                    }
                </div>

            </Modal.Body>

            <Modal.Footer>
                <Button
                    style={{ marginRight: "auto" }}
                    onClick={props.onClose}
                    variant="secondary">
                    <Icon value={"times"} className={"mr-1"} />
                    <FormattedMessage
                        id="common.search_filter.btn.close"
                        defaultMessage="Close"
                    />
                </Button>

                <DropdownButton
                    as={ButtonGroup}
                    onSelect={handleTemplateSelect}
                    title={selectedTemplate ? selectedTemplate.getName() : ""}
                >
                    {Object.entries(templatesList).map(([key, value]) =>
                        <Dropdown.Item key={key} eventKey={key} value={value} className={"text-" + key}>
                            {value.getName()}
                        </Dropdown.Item>
                    )}
                </DropdownButton>

                <ActionButton
                    onClick={sendSmsMessage}
                    className="btn btn-primary"
                    isLoading={isLoading}
                    disabled={disableSubmit}>
                    <Icon value={"paper-plane"} className={"mr-1"} />
                    <FormattedMessage
                        id="send_sms.modal.modal.btn.send_sms"
                        defaultMessage="Send an SMS"
                    />
                </ActionButton>
            </Modal.Footer>
        </Modal >
    )
}

export default injectIntl(SendSmsModal)



// WEBPACK FOOTER //
// ./src/common/sms/SendSmsModal.tsx